import React, { useCallback, useState } from "react";
import { Star } from "lucide-react";
import { mergeProps, useLongPress, usePress } from "react-aria";
import { toast } from "sonner";

import type {
	Menu,
	MenuCategory,
	MenuItem,
	Product,
} from "@workspace/product-catalog-types";
import { Button } from "@workspace/ui/components/button";
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
} from "@workspace/ui/components/dialog";
import { ScrollArea, ScrollBar } from "@workspace/ui/components/scroll-area";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@workspace/ui/components/select";
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from "@workspace/ui/components/tooltip";
import { cn } from "@workspace/ui/lib/utils";
import type { StaffMember } from "@workspace/venue-management-staff";

import {
	ACTION_LABELS,
	actionsFor,
	blockedMessage,
	canAddToOrder,
	overrideExpiryLabel,
	pillFor,
	statusFor,
	subjectsFor,
	type EightySixAction,
} from "../eighty-six";
import { useEightySix } from "../eighty-six-context";
import { EightySixProvider } from "../eighty-six-provider";
import { useEposContext } from "../epos-context";
import { useEightySixSource } from "../use-eighty-six";
import { EposHeaderUser } from "./epos-header";
import { visibleCategories } from "./visible-categories";

interface EposProductsProps {
	onProductSelect: (
		product: Product,
		variantId: string,
		quantity?: number,
	) => void;
}

interface EposCategorySelectorProps {
	categories: MenuCategory[];
	selectedCategory?: MenuCategory | null;
	onCategorySelect: (category: MenuCategory) => void;
	parentId?: string;
	open?: boolean;
}

function EposButton({
	bgColor,
	isActive,
	variant,
	className,
	...props
}: React.ComponentProps<typeof Button> & {
	bgColor?: string | null | undefined;
	isActive?: boolean;
}) {
	return (
		<Button
			variant={variant || (isActive ? "default" : "outline")}
			style={
				!isActive
					? {
							backgroundColor: bgColor || undefined,
							color: bgColor ? getContrastingTextColor(bgColor) : undefined,
						}
					: undefined
			}
			aria-pressed={isActive}
			className={cn(
				"flex h-14 min-w-32 flex-col items-center justify-center text-center",
				className,
			)}
			{...props}
		/>
	);
}

export function MenuCategoryRow({
	categories,
	selectedCategory,
	onCategorySelect,
}: EposCategorySelectorProps) {
	// Find if any category in this row should be considered "active" (selected or has selected descendant)
	const getActiveCategory = () => {
		if (!selectedCategory) return null;

		// Check if selectedCategory is directly in this row
		const directMatch = categories.find(
			(cat) => cat.id === selectedCategory.id,
		);
		if (directMatch) return directMatch;

		// Check if selectedCategory is a descendant of any category in this row
		const findCategoryWithDescendant = (
			cats: MenuCategory[],
			targetId: string,
		): MenuCategory | null => {
			for (const cat of cats) {
				if (cat.id === targetId) return cat;
				const found = findCategoryWithDescendant(cat.subCategories, targetId);
				if (found) return cat; // Return the ancestor, not the descendant
			}
			return null;
		};

		return findCategoryWithDescendant(categories, selectedCategory.id);
	};

	const activeCategory = getActiveCategory();

	// Check if a category is selected or has a selected descendant
	const isCategoryActiveOrParent = (category: MenuCategory): boolean => {
		if (category.id === selectedCategory?.id) return true;
		if (!selectedCategory) return false;

		const hasSelectedDescendant = (
			cat: MenuCategory,
			targetId: string,
		): boolean => {
			return cat.subCategories.some(
				(subCat) =>
					subCat.id === targetId || hasSelectedDescendant(subCat, targetId),
			);
		};

		return hasSelectedDescendant(category, selectedCategory.id);
	};

	return (
		<>
			<div
				className="flex flex-col"
				role="navigation"
				aria-label="Menu categories"
			>
				<div className="grid h-full grid-cols-1 overflow-hidden md:grid-rows-[auto_1fr]">
					<div className="overflow-auto border-b p-2">
						<div
							className="flex gap-1 flex-wrap"
							role="group"
							aria-label="Category buttons"
						>
							{visibleCategories(categories).map((category) => {
								return (
									<EposButton
										key={category.id}
										bgColor={category.color}
										isActive={isCategoryActiveOrParent(category)}
										onClick={() => {
											return onCategorySelect(category);
										}}
										aria-label={`Select ${category.name} category`}
									>
										<span className="text-xl font-medium">{category.name}</span>
									</EposButton>
								);
							})}
						</div>
					</div>
				</div>
			</div>
			{activeCategory &&
				visibleCategories(activeCategory.subCategories).length > 0 && (
					<MenuCategoryRow
						categories={activeCategory.subCategories}
						selectedCategory={selectedCategory}
						onCategorySelect={onCategorySelect}
						parentId={activeCategory.id}
					/>
				)}
		</>
	);
}

/**
 * HA-591 — the 86 actions, inside the dialog the long press already opens.
 *
 * <para>A section rather than a gesture of its own. Long press is taken by the quantity keypad,
 * and one gesture that means "what do you want to do with this item" is learnable where two
 * similar presses on the same tile are not.</para>
 *
 * <para>Renders nothing at all when the person holds neither grant, or when every action would be
 * a no-op — an empty heading over no buttons reads as something failing to load.</para>
 */
function EightySixActions({
	productId,
	variantId,
	onDone,
}: {
	productId: string;
	variantId: string | null;
	onDone: () => void;
}) {
	const { statuses, permissions, apply, pending } = useEightySix();
	const status = statusFor(statuses, productId, variantId);
	const actions = actionsFor(status, permissions);

	const expiry = overrideExpiryLabel(status, (iso) =>
		new Date(iso).toLocaleTimeString([], {
			hour: "2-digit",
			minute: "2-digit",
		}),
	);

	if (actions.length === 0) return null;

	const run = async (action: EightySixAction) => {
		await apply(action, { productId, variantId });
		onDone();
	};

	return (
		<div className="space-y-2 border-t pt-4">
			<div className="text-center text-sm text-muted-foreground">
				{status?.manualNote ?? expiry ?? "Stock"}
			</div>

			{actions.map((action) => (
				<Button
					key={action}
					variant={action === "set" ? "destructive" : "outline"}
					className="h-12 w-full text-base font-semibold"
					disabled={pending}
					onClick={() => void run(action)}
				>
					{ACTION_LABELS[action]}
				</Button>
			))}
		</div>
	);
}

interface QuantityKeypadProps {
	isOpen: boolean;
	onClose: () => void;
	onConfirm: (quantity: number) => void;
	productName: string;
	productId: string | null;
	variantId: string | null;
}

function QuantityKeypad({
	isOpen,
	onClose,
	onConfirm,
	productName,
	productId,
	variantId,
}: QuantityKeypadProps) {
	const [quantity, setQuantity] = useState("");

	const handleNumberPress = useCallback((num: string) => {
		setQuantity((prev) => {
			// If we're starting fresh (empty) or current value is "0", replace with the new number
			if (prev === "" || prev === "0") return num;
			// Limit to 3 digits
			if (prev.length >= 3) return prev;
			return prev + num;
		});
	}, []);

	const handleClear = useCallback(() => {
		setQuantity("");
	}, []);

	const handleBackspace = useCallback(() => {
		setQuantity((prev) => {
			// If only one character or empty, clear it
			if (prev.length <= 1) return "";
			return prev.slice(0, -1);
		});
	}, []);

	const handleConfirm = useCallback(async () => {
		const qty = parseInt(quantity, 10) || 0;
		if (qty > 0) {
			await onConfirm(qty);
			onClose();
		}
		// If quantity is 0 or empty, just close without adding anything
		if (qty === 0) {
			onClose();
		}
	}, [quantity, onConfirm, onClose]);

	const handleOpenChange = useCallback(
		(open: boolean) => {
			if (!open) {
				onClose();
			}
		},
		[onClose],
	);

	// Reset quantity when dialog opens
	React.useEffect(() => {
		if (isOpen) {
			// eslint-disable-next-line react-hooks/set-state-in-effect -- HA-738: react-hooks 7.1 finding, refactor deferred
			setQuantity("");
		}
	}, [isOpen]);

	const keypadButtons = [
		["1", "2", "3"],
		["4", "5", "6"],
		["7", "8", "9"],
		["C", "0", "⌫"],
	];

	return (
		<Dialog open={isOpen} onOpenChange={handleOpenChange}>
			<DialogContent className="sm:max-w-md" showCloseButton={false}>
				<DialogHeader>
					<DialogTitle className="text-center">Select Quantity</DialogTitle>
					<div className="text-center text-sm text-muted-foreground">
						{productName}
					</div>
				</DialogHeader>

				<div className="space-y-4">
					{/* Quantity Display */}
					<div className="text-center">
						<div className="text-6xl font-bold text-primary p-4 bg-muted rounded-lg">
							{quantity || "0"}
						</div>
					</div>

					{/* Keypad */}
					<div className="grid grid-cols-3 gap-2">
						{keypadButtons.flat().map((key, index) => (
							<Button
								// eslint-disable-next-line @eslint-react/no-array-index-key -- HA-743: a fixed keypad layout; the position is the identity
								key={index}
								variant={key === "C" ? "destructive" : "outline"}
								className="h-12 text-lg font-semibold"
								onClick={() => {
									if (key === "C") {
										handleClear();
									} else if (key === "⌫") {
										handleBackspace();
									} else {
										handleNumberPress(key);
									}
								}}
							>
								{key}
							</Button>
						))}
					</div>

					{/* Action Buttons */}
					<div className="flex gap-2">
						<Button variant="outline" className="flex-1" onClick={onClose}>
							Cancel
						</Button>
						<Button className="flex-1" onClick={handleConfirm}>
							{(parseInt(quantity, 10) || 0) > 0 ? `Add ${quantity}` : "Close"}
						</Button>
					</div>

					{productId && (
						<EightySixActions
							productId={productId}
							variantId={variantId}
							onDone={onClose}
						/>
					)}
				</div>
			</DialogContent>
		</Dialog>
	);
}

interface EposProductSelectorProps {
	menuItems: MenuItem[];
	onProductSelect: (
		product: Product,
		variantId: string,
		quantity?: number,
	) => void;
	products: Product[];
}

function ProductSelector({
	menuItems,
	onProductSelect,
	products,
}: EposProductSelectorProps) {
	const [quantityDialog, setQuantityDialog] = useState<{
		isOpen: boolean;
		product: Product | null;
		variantId: string | null;
		productName: string;
	}>({
		isOpen: false,
		product: null,
		variantId: null,
		productName: "",
	});

	const handleQuantityConfirm = useCallback(
		async (quantity: number) => {
			if (quantityDialog.product && quantityDialog.variantId) {
				await onProductSelect(
					quantityDialog.product,
					quantityDialog.variantId,
					quantity,
				);
			}
			setQuantityDialog({
				isOpen: false,
				product: null,
				variantId: null,
				productName: "",
			});
		},
		[quantityDialog, onProductSelect],
	);

	const handleQuantityClose = useCallback(() => {
		setQuantityDialog({
			isOpen: false,
			product: null,
			variantId: null,
			productName: "",
		});
	}, []);

	return (
		<div className="flex flex-col" role="region" aria-label="Product selection">
			<div className="grid h-full grid-cols-1 overflow-hidden md:grid-rows-[auto_1fr]">
				<div className="overflow-auto p-2">
					<div
						className="flex gap-1 flex-wrap"
						role="group"
						aria-label="Product buttons"
					>
						{menuItems.length > 0 &&
							menuItems.map((menuItem) => {
								const parentProduct = products.find(
									(x) => x.id === menuItem.productId,
								);

								if (!parentProduct) return;

								const variants =
									(menuItem.variantSelectionMode === "All"
										? parentProduct.variants
										: parentProduct.variants?.filter((x) =>
												menuItem.productVariantIds?.includes(x.id),
											)) || [];

								return (
									<React.Fragment key={menuItem.id}>
										{variants.map((variant) => (
											<ProductButton
												key={variant.id}
												product={parentProduct}
												variant={variant}
												onProductSelect={onProductSelect}
												onLongPress={(product, variantId, productName) => {
													setQuantityDialog({
														isOpen: true,
														product,
														variantId,
														productName,
													});
												}}
											/>
										))}
									</React.Fragment>
								);
							})}
					</div>
				</div>
			</div>
			<QuantityKeypad
				isOpen={quantityDialog.isOpen}
				onClose={handleQuantityClose}
				onConfirm={handleQuantityConfirm}
				productName={quantityDialog.productName}
				productId={quantityDialog.product?.id ?? null}
				variantId={quantityDialog.variantId}
			/>
		</div>
	);
}

export const DISPLAY_ONLY_MESSAGE = "Display only — not for sale";

interface ProductButtonProps {
	product: Product;
	variant: { id: string; name: string; buttonColor?: string | null };
	onProductSelect: (
		product: Product,
		variantId: string,
		quantity?: number,
	) => void;
	onLongPress: (
		product: Product,
		variantId: string,
		productName: string,
	) => void;
}

export function ProductButton({
	product,
	variant,
	onProductSelect,
	onLongPress,
}: ProductButtonProps) {
	// HA-591. Absent statuses mean sellable, so a terminal that has not loaded them — or a screen
	// that never will — behaves exactly as it did before this feature.
	const { statuses } = useEightySix();
	const status = statusFor(statuses, product.id, variant.id);
	const pill = pillFor(status);
	// HA-600 AC7 — a display-only product is on the menu but never sold: shown, inert, and saying
	// why when tapped. The server refuses the line too (PRODUCT_DISPLAY_ONLY).
	const displayOnly = product.displayOnly === true;
	const [displayOnlyHint, setDisplayOnlyHint] = useState(false);
	React.useEffect(() => {
		if (!displayOnlyHint) return;
		const hide = setTimeout(() => setDisplayOnlyHint(false), 2500);
		return () => clearTimeout(hide);
	}, [displayOnlyHint]);
	const blocked = displayOnly || !canAddToOrder(status);

	const { pressProps } = usePress({
		onPress: () => {
			if (displayOnly) {
				setDisplayOnlyHint(true);
				return;
			}

			if (blocked) {
				// Said out loud rather than a tile that silently does nothing, which reads as the
				// till being broken.
				const message = blockedMessage(status);
				if (message) toast.warning(message);

				return;
			}

			onProductSelect(product, variant.id);
		},
	});

	// Not for a tile that can't be ordered: the quantity keypad would only add what a tap can't.
	const { longPressProps } = useLongPress({
		isDisabled: blocked,
		onLongPress: () => onLongPress(product, variant.id, variant.name),
	});

	const mergedProps = mergeProps(pressProps, longPressProps);

	const button = (
		<EposButton
			{...mergedProps}
			aria-disabled={displayOnly || undefined}
			bgColor={variant.buttonColor}
			variant="outline"
			className={cn(
				"flex h-14 min-w-32 flex-col items-center justify-center text-center",
				// Dimmed, not hidden: the kitchen still needs to see what is off, and a dish that
				// vanishes from the menu is a dish nobody can ask about.
				blocked && "opacity-60",
			)}
			aria-label={
				displayOnly
					? `${variant.name}. ${DISPLAY_ONLY_MESSAGE}.`
					: pill
						? `${variant.name}. ${pill.label}.`
						: `Add ${variant.name} to order. Long press for quantity selection.`
			}
		>
			<span className="text-xl font-medium">{variant.name}</span>
			{pill && (
				<span
					className={cn(
						"mt-0.5 rounded px-1 text-[10px] font-semibold uppercase leading-tight",
						pill.tone === "manual" &&
							"bg-destructive text-destructive-foreground",
						pill.tone === "auto" && "bg-warning text-warning-foreground",
						pill.tone === "override" && "border border-success text-success",
					)}
				>
					{pill.label}
				</span>
			)}
		</EposButton>
	);

	if (!displayOnly) return button;

	return (
		<Tooltip open={displayOnlyHint} onOpenChange={setDisplayOnlyHint}>
			<TooltipTrigger render={button} />
			<TooltipContent>{DISPLAY_ONLY_MESSAGE}</TooltipContent>
		</Tooltip>
	);
}

function MenuBrowser({
	menu,
	onCategorySelect,
	selectedCategory,
	activeMenus,
	onMenuSelect,
	staffMember,
}: {
	menu: Menu | null;
	onCategorySelect: (category: MenuCategory | null) => void;
	selectedCategory?: MenuCategory | null;
	activeMenus?: Menu[];
	onMenuSelect?: (menu: Menu) => void;
	staffMember: StaffMember | null | undefined;
}) {
	if (!menu) {
		return (
			<div className="flex h-12 items-center border-b px-4" role="banner">
				<span className="text-lg font-medium">No Menu Selected</span>
			</div>
		);
	}

	const categories = visibleCategories(menu.categories[0]?.subCategories || []);
	const showMenuSwitcher = activeMenus && activeMenus.length > 1;

	return (
		<>
			<div
				className="flex h-12 items-center border-b px-4 justify-between"
				role="banner"
				aria-label="Menu header"
			>
				{showMenuSwitcher ? (
					<div className="flex items-center gap-2">
						<span className="text-lg font-medium" id="menu-label">
							Menu:
						</span>
						<Select
							value={menu.id}
							onValueChange={(value) => {
								const selectedMenu = activeMenus.find((m) => m.id === value);
								if (selectedMenu && onMenuSelect) {
									onMenuSelect(selectedMenu);
								}
							}}
							aria-labelledby="menu-label"
						>
							<SelectTrigger aria-label="Select menu">
								<SelectValue />
							</SelectTrigger>
							<SelectContent role="listbox">
								{activeMenus.map((activeMenu) => (
									<SelectItem
										key={activeMenu.id}
										value={activeMenu.id}
										role="option"
										aria-label={`${activeMenu.name}${activeMenu.isDefault ? " (default)" : ""}`}
									>
										<div className="flex items-center gap-2">
											{activeMenu.isDefault && (
												<Star
													className="h-4 w-4 fill-current"
													aria-hidden="true"
												/>
											)}
											<span>{activeMenu.name}</span>
										</div>
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>
				) : (
					<div className="flex items-center gap-2">
						{menu.isDefault && (
							<Star className="h-4 w-4 fill-current" aria-hidden="true" />
						)}
						<span className="text-lg font-medium">{menu.name}</span>
					</div>
				)}
				<EposHeaderUser className="ml-4" staffMember={staffMember} />
			</div>
			{categories.length === 0 && (
				<div
					className="flex h-12 items-center border-b px-4"
					role="status"
					aria-live="polite"
				>
					<span className="text-lg font-medium">No Categories</span>
				</div>
			)}
			{categories.length > 0 && (
				<MenuCategoryRow
					selectedCategory={selectedCategory}
					categories={categories}
					onCategorySelect={onCategorySelect}
					parentId="root"
				/>
			)}
		</>
	);
}

function getContrastingTextColor(backgroundColor: string): string | undefined {
	if (!backgroundColor) return undefined;

	// Normalize and parse color
	const parseColor = (
		color: string,
	): { r: number; g: number; b: number } | null => {
		// Handle hex colors (3, 4, 6, 8 digits)
		const hexMatch = color.match(/^#?([a-f\d]{3,8})$/i);
		if (hexMatch) {
			const hex = hexMatch[1];
			if (hex?.length === 3) {
				// #rgb -> #rrggbb
				return {
					r: parseInt(hex[0]! + hex[0]!, 16),
					g: parseInt(hex[1]! + hex[1]!, 16),
					b: parseInt(hex[2]! + hex[2]!, 16),
				};
			} else if (hex?.length === 6) {
				// #rrggbb
				return {
					r: parseInt(hex.substring(0, 2), 16),
					g: parseInt(hex.substring(2, 4), 16),
					b: parseInt(hex.substring(4, 6), 16),
				};
			}
		}

		// Handle rgb/rgba
		const rgbMatch = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
		if (rgbMatch) {
			return {
				r: parseInt(rgbMatch[1]!, 10),
				g: parseInt(rgbMatch[2]!, 10),
				b: parseInt(rgbMatch[3]!, 10),
			};
		}

		return null;
	};

	const rgb = parseColor(backgroundColor);
	if (!rgb) return "#000000"; // Default to black if parsing fails

	// WCAG 2.1 relative luminance calculation
	const getRelativeLuminance = (r: number, g: number, b: number): number => {
		const [rs, gs, bs] = [r, g, b].map((c) => {
			const sRGB = c / 255;
			return sRGB <= 0.03928
				? sRGB / 12.92
				: Math.pow((sRGB + 0.055) / 1.055, 2.4);
		});
		return 0.2126 * rs! + 0.7152 * gs! + 0.0722 * bs!;
	};

	const luminance = getRelativeLuminance(rgb.r, rgb.g, rgb.b);

	// Use WCAG threshold for better accessibility
	return luminance > 0.179 ? "#000000" : "#ffffff";
}

/**
 * HA-591 — asks the server about the tiles this screen is about to draw, and puts the answer where
 * they can read it.
 *
 * <para>Mounted around the grid rather than at the top of the terminal so the question is scoped to
 * one category's worth of dishes: the answer is derived per dish, and a screen showing forty should
 * not make the server derive a thousand.</para>
 */
function EightySixMenu({
	menuItems,
	products,
	children,
}: React.PropsWithChildren<{
	menuItems: MenuItem[];
	products: Product[];
}>) {
	const value = useEightySixSource(subjectsFor(menuItems, products));

	return <EightySixProvider {...value}>{children}</EightySixProvider>;
}

export function EposProducts({ onProductSelect }: EposProductsProps) {
	const {
		selectedCategory,
		setSelectedCategory,
		activeMenus,
		currentMenu,
		setCurrentMenu,

		products,
		selectedStaff,
	} = useEposContext();

	const filteredProducts = selectedCategory?.items || [];

	const handleCategorySelect = (category: MenuCategory | null | undefined) => {
		setSelectedCategory(category || null);
	};

	const handleMenuSelect = (menu: Menu) => {
		setCurrentMenu(menu);
		setSelectedCategory(null);
	};

	return (
		<div
			className="flex h-full flex-col"
			role="main"
			aria-label="Product catalog"
		>
			<MenuBrowser
				menu={currentMenu}
				onCategorySelect={handleCategorySelect}
				selectedCategory={selectedCategory}
				activeMenus={activeMenus}
				onMenuSelect={handleMenuSelect}
				staffMember={selectedStaff}
			/>
			{filteredProducts.length > 0 && (
				<ScrollArea className="overflow-hidden h-full">
					<EightySixMenu menuItems={filteredProducts} products={products}>
						<ProductSelector
							menuItems={filteredProducts}
							onProductSelect={onProductSelect}
							products={products}
						/>
					</EightySixMenu>
					<ScrollBar />
				</ScrollArea>
			)}
		</div>
	);
}
